// DiagiaMine — "Mes diagnostics" page.
//
// Lists what the authenticated user has personally generated. Each row links
// to the rendered document. Empty state nudges the user to start one.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaPlusCircle, FaFileAlt } from 'react-icons/fa';
import useDiagiaApi from '../hooks/useDiagiaApi';

export default function DiagiaMine() {
  const { t } = useTranslation();
  const api = useDiagiaApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.listMyDiagnostics()
      .then((rows) => { if (!cancelled) setItems(rows); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || 'Erreur de chargement');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api]);

  return (
    <div className="dia-page">
      <header className="dia-page-head">
        <div>
          <h1>{t('diagia.nav.mine', 'Mes diagnostics')}</h1>
          <p>{t('diagia.mine.sub', 'Tous les cadrages que vous avez générés.')}</p>
        </div>
        <Link to="/diagia/new" className="dia-cta dia-cta-primary">
          <FaPlusCircle /> Nouveau diagnostic
        </Link>
      </header>

      {loading && <p className="dia-empty">{t('diagia.home.loading', 'Chargement…')}</p>}
      {!loading && error && <p className="dia-error">{error}</p>}
      {!loading && !error && items.length === 0 && (
        <div className="dia-empty-card">
          <FaFileAlt size={28} />
          <h3>{t('diagia.mine.empty_title', "Aucun diagnostic pour l'instant")}</h3>
          <p>{t('diagia.mine.empty_body', 'Le premier prend 3 minutes. Lance-toi.')}</p>
          <Link to="/diagia/new" className="dia-cta dia-cta-primary">
            Commencer maintenant
          </Link>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="dia-card">
          <table className="dia-table">
            <thead>
              <tr>
                <th>{t('diagia.home.th_title', 'Titre')}</th>
                <th>{t('diagia.home.th_score', 'Score')}</th>
                <th>{t('diagia.home.th_lang', 'Langue')}</th>
                <th>{t('diagia.home.th_created', 'Créé le')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id}>
                  <td className="dia-table-title">
                    <Link to={`/diagia/diagnostics/${d.id}`}>{d.title || t('diagia.home.untitled', 'Sans titre')}</Link>
                  </td>
                  <td>
                    {typeof d.score === 'number' ? (
                      <span className="dia-score-pill">{d.score}/100</span>
                    ) : (
                      <span className="dia-table-muted">—</span>
                    )}
                  </td>
                  <td>{(d.language || '').toUpperCase() || '—'}</td>
                  <td>{new Date(d.created_at).toLocaleDateString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
